/**
 * Photographing the page the reporter is looking at.
 *
 * snapDOM serializes the live DOM to an SVG and lets the browser rasterize it.
 * There is no permission prompt and no share dialog, which is the whole point:
 * the browser's own tab capture (`getDisplayMedia`) always shows its picker —
 * `preferCurrentTab` only promotes "This tab" to the front of the list, it
 * cannot skip the confirmation, and `getViewportMedia`, the API that would
 * have made an own-tab capture promptless, was never shipped by any browser.
 * Being promptless also means this works on a phone, and in the e2e suite,
 * where a share picker needs a user gesture that a test cannot give.
 */
import { snapdom } from "@zumer/snapdom";

/**
 * Marks every node the report UI owns, so the reporter's picture never
 * contains the reporting UI itself.
 */
export const CAPTURE_UI_ATTRIBUTE = "data-support-capture-ui";
const CAPTURE_UI_SELECTOR = `[${CAPTURE_UI_ATTRIBUTE}]`;

export interface CaptureFrame {
	blob: Blob;
	width: number;
	height: number;
}

export const capturePage = async (): Promise<CaptureFrame> => {
	const canvas = await snapdom.toCanvas(document.body, {
		clip: "viewport",
		exclude: [CAPTURE_UI_SELECTOR],
		excludeMode: "remove",
		backgroundColor: getComputedStyle(document.body).backgroundColor,
		dpr: window.devicePixelRatio,
		// Measures the clone against the live DOM instead of guessing box sizes.
		// It roughly doubles capture time, which is still imperceptible here,
		// and without it text in table cells re-wraps under font fallback —
		// exactly the detail a support picture is supposed to preserve.
		reconcile: true,
	});
	const blob = await new Promise<Blob | null>((resolve) =>
		canvas.toBlob(resolve, "image/png")
	);
	if (!blob) {
		throw new Error("The captured picture could not be encoded");
	}
	return { blob, width: canvas.width, height: canvas.height };
};

/**
 * Photographs something that is not this page: another tab, another window, or
 * the whole screen. The browser's picker is the point here rather than a cost —
 * the reporter is choosing a surface only they can see — so no `preferCurrentTab`
 * hint, every surface stays on offer.
 *
 * Must be called straight from the click: `getDisplayMedia` requires user
 * activation, so nothing may be awaited before it.
 */
export const captureOtherSurface = async (): Promise<CaptureFrame> => {
	const stream = await navigator.mediaDevices.getDisplayMedia({
		video: true,
		audio: false,
	});
	const video = document.createElement("video");
	video.srcObject = stream;
	try {
		await new Promise<void>((resolve, reject) => {
			video.onloadedmetadata = () => resolve();
			video.onerror = () => reject(new Error("The capture produced no video"));
			video.play().catch(reject);
		});
		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("2d canvas context unavailable");
		}
		context.drawImage(video, 0, 0);
		const blob = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, "image/png")
		);
		if (!blob) {
			throw new Error("The captured picture could not be encoded");
		}
		return { blob, width: canvas.width, height: canvas.height };
	} finally {
		stream.getTracks().forEach((track) => track.stop());
	}
};

/**
 * Mobile browsers expose `getDisplayMedia` but reject every call, and it only
 * exists in a secure context — so the tile is hidden rather than failing in the
 * reporter's face.
 */
export const isOtherSurfaceCaptureSupported = (): boolean => {
	if (!window.isSecureContext) return false;
	if (!navigator.mediaDevices?.getDisplayMedia) return false;
	if (/Android|webOS|iPhone|iPad|iPod|IEMobile/i.test(navigator.userAgent)) {
		return false;
	}
	return !(/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
};

/** Lets the browser paint the hidden report UI before the shutter goes off. */
export const nextPaint = (): Promise<void> =>
	new Promise((resolve) =>
		requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
	);

/**
 * The picture in a paste, if there is one.
 *
 * The fourth way an image gets into a report, and the one people reach for
 * without being told: Win+Shift+S, Cmd+Ctrl+Shift+4, or a copied image from
 * anywhere else, then Ctrl+V into the box. It costs no control on screen,
 * which is why it is worth having even beside three explicit ones.
 *
 * Returns null for an ordinary text paste, which must go on behaving as a
 * text paste — hence no preventDefault here; the caller does it only when
 * this hands back a file.
 */
export const imageFromPaste = (clipboard: DataTransfer | null): File | null => {
	for (const item of clipboard?.items ?? []) {
		if (!item.type.startsWith("image/")) continue;
		const file = item.getAsFile();
		if (file) return file;
	}
	return null;
};
